import React, { useState, useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import moment from 'moment';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import isEmpty from 'lodash/isEmpty';

import Step1 from './assignment-edit-step-1';
import Step2 from './assignment-edit-step-2';
import Step3 from './assignment-edit-step-3';
import Step4 from './assignment-edit-step-4';
import Step5 from './assignment-edit-step-5';

import Loading from '../../../components/loading/loading';
import StepNavigation from '../../../components/step-navigation/step-navigation';
import {
  getAssignment, createAssignment, updateAssignment
} from '../../../queries/admin-queries';
import { getFormHelp, formHelpQuerySettings } from '../../../queries/help';
import defaultAssignment from '../../../common/assignment-defaults';
import styles from './assignment-edit.scss';

const AssignmentEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isEditMode = location.pathname.endsWith('/edit');
  const dateNow = moment.utc().local().format('YYYY-MM-DD HH:mm');
  const [currentStep, setCurrentStep] = useState(1);
  const [canClose, setCanClose] = useState(true);

  // GET ASSIGNMENT
  const {
    isLoading,
    isSuccess,
    data: assignmentDetail
  } = useQuery(['admin-assignment', id], () => getAssignment(id), { enabled: isEditMode });

  const help = useQuery(['form-help', 'assignment-edit'], () => getFormHelp('assignment-edit'), formHelpQuerySettings);

  // UPDATE ASSIGNMENT
  const {
    mutate: mutateUpdateAssigment,
  } = useMutation(['admin-assignment', id], (values) => updateAssignment(id, values));

  const onUpdateSubmit = (formData) => {
    mutateUpdateAssigment(formData, {
      onSuccess: (data, values) => {
        console.log("Updated assignment!")
        console.dir(formData);
        console.dir(data);
        console.dir(values);

        // Clear the assignment cache for faculty
        queryClient.invalidateQueries(['admin-assignment', id]);
      },
      onError: (error, variables, context) => {
        console.log(error.response, variables, context);
      },
    });
  };

  // CREATE ASSIGNMENT
  const {
    mutate: mutateCreateAssigment,
  } = useMutation('admin-assignment', (values) => createAssignment(values));

  const onCreateSubmit = (formData) => {
    mutateCreateAssigment(formData, {
      onSuccess: (data, values) => {
        console.log("Created assignment!")
        console.dir(data);
        console.dir(values);
        let url = `/assignments/${data.id}/edit`;
        console.log(`URL: ${url}`);
        history.push(url);
      },
      onError: (error, variables, context) => {
        console.log(error.response, variables, context);
      },
    });
  };

  let editOrCreateAssignment = onUpdateSubmit;

  if (!isEditMode) {
    editOrCreateAssignment = onCreateSubmit;
  }

  const [assignmentDraft, setAssignmentDraft] = useState(defaultAssignment(id));

  useEffect(() => {
    if (isEditMode) {
      setAssignmentDraft({ ...assignmentDetail });
    }
  }, [isSuccess, assignmentDetail]);

  const [direction, setDirection] = useState('forward');
  const transition = useTransition(currentStep, (currentStep) => currentStep, {
    initial: null,
    from: {
      opacity: 0,
      transform: `translate3d(${direction === 'forward' ? '100%' : '-100%'},0, 0)`
    },
    enter: {
      opacity: 1,
      transform: `translate3d(${direction === 'forward' ? '0%' : '0%'}, 0, 0)`
    },
    leave: {
      opacity: 0,
      transform: `translate3d(${direction === 'forward' ? '-100%' : '100%'}, 0, 0)`
    },
  });

  const goToStep = (step) => {
    setDirection('back');
    if (step < currentStep) {
      setCurrentStep(step);
    }
  };

  let steps = [
    { title: 'Welcome Page' },
    { title: 'Assignment' },
    { title: 'Platform Setup' },
    { title: 'Review Rubric' },
    { title: 'Score Review Rubric' }
  ];

  let skipStep5 = false;

  if (isEditMode && assignmentDraft.skip_feedback_state) {
    skipStep5 = true;
  }

  const goToNextStep = () => {
    setDirection('forward');
    let newStep = currentStep + 1;

    if (skipStep5 && newStep > 4) { newStep = 4; }
    if (!skipStep5 && newStep > 5) { newStep = 4; }
    setCurrentStep(newStep);
  };

  const updateAndSave = (assignmentData) => {
    // set the assignment draft to keep the old logic working
    setAssignmentDraft(assignmentData);

    // hit the API with a patch to update it for real
    editOrCreateAssignment(assignmentData);

    // Allow the page to be closed
    setCanClose(true);
  };

  if (isEditMode && isEmpty(assignmentDraft)) {
    return (
      <div>Loading</div>
    );
  }

  if (isLoading) {
    return (<Loading message="Loading asssignment..." />);
  }

  return (
    <div className={styles.container}>
      <StepNavigation
        currentStep={currentStep}
        goToStep={goToStep}
        steps={steps}
        nextType="submit"
        forForm="step-form"
        nextTitle="Save &amp; Continue"
        finalTitle="Save"
        skipStep5={skipStep5}
        canClose={canClose}
      />
      {transition.map(({ item, props, key }) => (
        <animated.div
          className={styles.stepContainer}
          style={props}
          key={key}
        >
          {currentStep === 1 && (
            <Step1
              currentStep={currentStep}
              goToNextStep={goToNextStep}
              assignmentDraft={assignmentDraft}
              setAssignmentDraft={updateAndSave}
              setCanClose={setCanClose}
              help={help}
            />
          )}
          {currentStep === 2 && (
            <Step2
              currentStep={currentStep}
              goToNextStep={goToNextStep}
              assignmentDraft={assignmentDraft}
              setAssignmentDraft={updateAndSave}
              setCanClose={setCanClose}
              help={help}
            />
          )}
          {currentStep === 3 && (
            <Step3
              currentStep={currentStep}
              goToNextStep={goToNextStep}
              assignmentDraft={assignmentDraft}
              setAssignmentDraft={updateAndSave}
              setCanClose={setCanClose}
              help={help}
            />
          )}
          {currentStep === 4 && (
            <Step4
              currentStep={currentStep}
              goToNextStep={goToNextStep}
              assignmentDraft={assignmentDraft}
              setAssignmentDraft={updateAndSave}
              updateAssignment={updateAndSave}
              skipStep4={skipStep5}
              setCanClose={setCanClose}
              help={help}
            />
          )}
          {currentStep === 5 && (
            <Step5
              currentStep={currentStep}
              goToNextStep={goToNextStep}
              assignmentDraft={assignmentDraft}
              updateAssignment={updateAndSave}
              skipStep={skipStep5}
              setCanClose={setCanClose}
              help={help}
            />
          )}
        </animated.div>
      ))}
    </div>
  );
};

export default AssignmentEdit;
